/**
 * **求值器源码** —— 数据包（可选加载的领域包）带求值逻辑的方式。
 *
 * 代码包把自己的求值器写成 TS 函数（`NodePackage.evaluators`）；
 * 数据包没法带函数，于是带**源码文本**，由这里编译成函数。
 * 两者最终是同一个东西，所以注册表能统一处理。
 *
 * ## 为什么有两种方言
 *
 * | 方言 | 是什么 | 跑在哪 | 沙箱 |
 * |---|---|---|---|
 * | `expr` | 一条受限表达式 | 我们自己的解释器 | **是**（不经过 JS 引擎） |
 * | `js` | 一段函数体 | `new Function` | 否（只受宿主 CSP 约束） |
 *
 * 用户定的方向是**先把 expr 做完整**，`js` 等真有需要再加 ——
 * 因为运行端和编辑器要共用同一层可移植的表达式，expr 正是那一层。
 *
 * ⚠️ 所以 `js` 现在**明确报「未实现」**，而不是静默失败：
 * 数据包里写了 `dialect: 'js'` 的节点，会在它自己身上显示一条
 * 「js 方言还没实现」的诊断，而不是变成一句莫名其妙的「未注册求值器」。
 *
 * ## 编译必须缓存
 *
 * 这条路径在求值的**热循环**里。每次求值都重新解析源码是灾难性的 ——
 * 所以按 `方言 + 源码` 缓存编译产物。缓存键带上方言：
 * 同一段文本在两种方言下含义不同。
 */
import type { NodeEvaluator } from './evaluate'
import { compileExpression, type ExprProblem } from './expr'

export type EvaluatorDialect = 'expr' | 'js'

export interface EvaluatorSource {
  dialect: EvaluatorDialect
  source: string
}

export type CompileResult =
  | { ok: true; evaluator: NodeEvaluator }
  | { ok: false; problems: ExprProblem[] }

const cache = new Map<string, CompileResult>()

/** 缓存大小上限：包的数量是有限的，超了说明有人在动态造源码 */
const MAX_CACHE = 512

export function compileEvaluatorSource(spec: EvaluatorSource): CompileResult {
  const key = `${spec.dialect}\u0000${spec.source}`
  const hit = cache.get(key)
  if (hit) return hit

  const result = compile(spec)
  if (cache.size >= MAX_CACHE) cache.clear()
  cache.set(key, result)
  return result
}

function compile(spec: EvaluatorSource): CompileResult {
  if (spec.dialect === 'js') {
    /*
     * ⚠️ 还没实现，而且**要说得具体**：作者写了 js 方言，
     * 他需要知道的是「换成语义等价的 expr」或者「等 js 方言上线」，
     * 不是「未注册求值器」。
     */
    return {
      ok: false,
      problems: [
        {
          message:
            'js 方言还没实现（当前只有 expr）。' +
            '把逻辑改写成一条 expr 表达式即可；js 方言会在需要时补上。',
          at: 0,
        },
      ],
    }
  }

  const compiled = compileExpression(spec.source)
  return compiled.ok ? { ok: true, evaluator: compiled.evaluator } : compiled
}

/**
 * 编译失败时用的**占位求值器**：一调用就抛，消息里带编译期问题。
 *
 * ⚠️ 为什么不干脆不注册：那样 `evaluateGraph` 只会报一句
 * 「未注册求值器」，作者拿不到任何有用信息。抛出去则由它已有的
 * try/catch 变成**这个节点上的一条诊断**，而消息是**具体**的
 * （哪一行、什么问题）。同一套机制，信息量差一个数量级。
 */
export function brokenEvaluator(type: string, problems: readonly ExprProblem[]): NodeEvaluator {
  const detail = problems.map((p) => p.message).join('；')
  return () => {
    throw new Error(`节点「${type}」的求值器源码有问题：${detail}`)
  }
}

/** 测试用：清空编译缓存 */
export function clearEvaluatorCache(): void {
  cache.clear()
}
